import { type PortalOrganizationInfo, getOrgBranding } from '@/services/portal.service'
import { useQuery } from '@tanstack/react-query'

// Subdomains reserved for first-party services — never treated as org slugs.
// Includes the `staging` label so the bare staging.pelgo.com host (and any
// other reserved subdomain on either stage) doesn't get mis-resolved to an
// "org" called staging/embrace/etc.
const RESERVED_SUBDOMAINS = new Set([
  'www',
  'api',
  'console',
  'embrace',
  'staging',
  'status',
  'i',
  'voice',
  'sms',
])

function getOrgSlugFromHostname(): string | null {
  // Dev override via env var
  // @ts-expect-error VITE_PORTAL_ORG_SLUG is a custom env var not in ImportMetaEnv
  const envSlug: string | undefined = import.meta.env.VITE_PORTAL_ORG_SLUG
  if (envSlug) return envSlug

  const hostname = window.location.hostname
  // Match: {slug}.pelgo.com, {slug}.staging.pelgo.com, or {slug}.localhost
  const parts = hostname.split('.')
  if (parts.length >= 2) {
    const slug = parts[0] ?? null
    if (slug && !RESERVED_SUBDOMAINS.has(slug)) {
      return slug
    }
  }
  return null
}

export function useOrgBranding() {
  const slug = getOrgSlugFromHostname()

  const query = useQuery<PortalOrganizationInfo>({
    queryKey: ['portal', 'org', slug],
    queryFn: () => getOrgBranding(slug as string),
    enabled: !!slug,
    staleTime: 1000 * 60 * 30, // 30 minutes - branding rarely changes
  })

  return {
    slug,
    orgName: query.data?.name ?? null,
    logoUrl: query.data?.logo_url ?? null,
    isLoading: query.isLoading,
    error: query.error,
  }
}
